/**
 * Terms acceptance service for GDPR compliance.
 *
 * Handles checking terms versions and accepting terms/privacy policy.
 */

import { apiGet, apiPost } from './api';

/**
 * Current versions of terms and privacy policy
 */
export interface CurrentVersions {
  terms_version: string;
  terms_effective_date: string;
  privacy_version: string;
  privacy_effective_date: string;
}

/**
 * User's terms acceptance status
 */
export interface AcceptanceStatus {
  // The API serialises this response in camelCase
  termsCurrent: boolean;
  privacyCurrent: boolean;
  needsAcceptance: boolean;
  userTermsVersion: string | null;
  userPrivacyVersion: string | null;
  currentTermsVersion: string;
  currentPrivacyVersion: string;
  termsSummary: string | null;
  privacySummary: string | null;
}

/**
 * Response after accepting terms
 */
export interface AcceptTermsResponse {
  success: boolean;
  message: string;
  terms_version: string;
  privacy_version: string;
  accepted_at: string;
}

/**
 * Get current versions of terms and privacy policy.
 * No authentication required.
 */
export async function getCurrentVersions(): Promise<CurrentVersions> {
  const response = await apiGet<CurrentVersions>('/api/legal/versions', { auth: false });
  if (response.error) {
    throw new Error(response.error.message);
  }
  return response.data!;
}

/**
 * Check if current user has accepted the latest terms.
 * Requires authentication.
 */
export async function getAcceptanceStatus(): Promise<AcceptanceStatus> {
  const response = await apiGet<AcceptanceStatus>('/api/legal/acceptance-status');
  if (response.error) {
    throw new Error(response.error.message);
  }
  return response.data!;
}

/**
 * Accept the current terms and privacy policy.
 * Requires authentication.
 */
export async function acceptTerms(): Promise<AcceptTermsResponse> {
  const response = await apiPost<AcceptTermsResponse>('/api/legal/accept', {
    terms_accepted: true,
    privacy_accepted: true,
  });
  if (response.error) {
    throw new Error(response.error.message);
  }
  return response.data!;
}
